import {WizardRef} from 'platform/components';

import {useCallback, useEffect} from 'react';

import {findFirstFocusableElement, findLastFocusableElement} from 'shared/web';

interface UseFormKeyboardNavigationProps {
  wizardRef: React.RefObject<WizardRef | null>;
  onStepChange?: (step: number) => void;
}
const isReactSelectElement = (element: HTMLElement): boolean =>
  // Check if the element or its parents have react-select specific classes
  !!(
    element.closest('.react-select__control') ||
    element.closest('.react-select__menu') ||
    element.closest('[class*="react-select"]')
  );

const getReactSelectContainer = (element: HTMLElement): HTMLElement | null => {
  // Find the outermost react-select container (usually has data-name attribute)
  const choiceContainer = element.closest('[data-name]') as HTMLElement;
  if (choiceContainer) {
    // Check if this is actually a react-select container
    const hasReactSelect = choiceContainer.querySelector('[class*="react-select"]');
    if (hasReactSelect) {
      return choiceContainer;
    }
  }

  // Fallback: traverse up to find the wrapper div containing react-select
  let current = element.parentElement;
  while (current && current !== document.body) {
    if (current.querySelector('.react-select__control')) {
      return current;
    }
    current = current.parentElement;
  }

  return null;
};

// Resolve the active tabpanel for the currently selected tab
const getActiveTabPanel = (): HTMLElement | null => {
  const activeTab = document.querySelector(
    '[role="tab"][aria-selected="true"]'
  ) as HTMLElement | null;
  if (!activeTab) {
    return null;
  }
  const controlsId = activeTab.getAttribute('aria-controls') || undefined;
  if (controlsId) {
    return document.getElementById(controlsId) as HTMLElement | null;
  }
  // Fallback: pick a visible tabpanel
  return document.querySelector(
    '[role="tabpanel"]:not([hidden]):not([aria-hidden="true"])'
  ) as HTMLElement | null;
};

const focusCurrentTab = () => {
  const activeTab = document.querySelector('[role="tab"][aria-selected="true"]') as HTMLElement;
  if (activeTab) {
    activeTab.focus();
  }
};
/**
 * Hook for handling keyboard navigation in vehicle forms
 *
 * Features:
 * - Tab from last input to next wizard step
 * - Shift+Tab from the first input returns focus to the active wizard tab
 * - Escape key to focus current wizard tab
 */
export function useFormKeyboardNavigation({
  wizardRef,
  onStepChange,
}: UseFormKeyboardNavigationProps) {
  // Handle keyboard navigation using React's event system
  const handleKeyDownCapture = useCallback(
    (event: React.KeyboardEvent) => {
      // Only handle Tab navigation between form and wizard tabs
      // Ignore Tab events that are part of IME composition or other non-navigation contexts
      if (event.key === 'Tab') {
        // Ignore modifier-assisted Tab shortcuts (let the browser/OS/AT handle them)
        if (event.altKey || event.ctrlKey || event.metaKey) {
          return;
        }

        // Don't intercept if this is part of an IME composition
        if (event.nativeEvent?.isComposing) {
          return;
        }

        // Don't intercept if the default behavior was already prevented by another handler
        if (event.defaultPrevented || event.nativeEvent?.defaultPrevented) {
          return;
        }

        // Only handle user-initiated Tab events (not programmatic events)
        // Skip this check in test environments where events are synthetic
        if (!event.isTrusted && process.env.NODE_ENV !== 'test') {
          return;
        }

        const activeElement = document.activeElement as HTMLElement;
        if (!activeElement) {
          return;
        }

        // If we're on a wizard tab
        if (activeElement.getAttribute('role') === 'tab') {
          if (!event.shiftKey) {
            // Tab forward from wizard tab - always go to first input in tab panel
            event.preventDefault();
            event.stopPropagation();

            // Focus the first input in the active tab panel
            const tabPanel = getActiveTabPanel();
            if (tabPanel) {
              const firstInput = findFirstFocusableElement(tabPanel);
              firstInput?.focus();
            }
          }
          // Shift+Tab goes backward naturally
          return;
        }

        // Handle Tab from last form input to wizard tabs (only forward Tab)
        if (!event.shiftKey) {
          // Check if we're in the active tab panel
          const tabPanel = getActiveTabPanel();
          if (!tabPanel || !tabPanel.contains(activeElement)) {
            return;
          }

          // Find the last focusable element using the utility
          const lastElement = findLastFocusableElement(tabPanel);

          // Special handling for react-select components
          let elementToCheck: HTMLElement | null = activeElement;
          if (isReactSelectElement(activeElement)) {
            // Get the react-select container to check if it's the last element
            elementToCheck = getReactSelectContainer(activeElement);
          }

          // Check if current focus is on or within the last element
          if (
            lastElement &&
            elementToCheck &&
            (elementToCheck === lastElement ||
              lastElement.contains(elementToCheck) ||
              elementToCheck.contains(lastElement))
          ) {
            event.preventDefault();
            event.stopPropagation();

            const currentStep = wizardRef.current?.getCurrentStep();
            // Try to move to the next wizard step
            wizardRef.current?.goToNextStep();

            // Check if step actually changed
            requestAnimationFrame(() => {
              const newStep = wizardRef.current?.getCurrentStep();
              if (newStep && newStep !== currentStep) {
                onStepChange?.(newStep);
              }
              if (newStep && newStep === currentStep) {
                focusCurrentTab();
              }
            });
          }
        }
      }
    },
    [wizardRef, onStepChange]
  );

  // Handle Escape key - use capture phase to intercept before react-select
  useEffect(() => {
    const handleEscapeCapture = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        focusCurrentTab();
      }
    };

    // Use capture phase (true) to get event before react-select stops it
    document.addEventListener('keydown', handleEscapeCapture, true);

    return () => {
      document.removeEventListener('keydown', handleEscapeCapture, true);
    };
  }, []);

  // Return the handler to be attached to React components
  return {
    onKeyDownCapture: handleKeyDownCapture,
  };
}
